import { useEffect, useRef } from 'react'

interface WaveformVisualizerProps {
  analyserNode: AnalyserNode | null
  isActive: boolean
  width?: number
  height?: number
  barWidth?: number
  barGap?: number
  minBarHeight?: number
  maxBars?: number
  backgroundColor?: string
  barColor?: string
  inactiveBarColor?: string
  smoothingFactor?: number
  className?: string
}

// all hardcoded colors are here due to the way the waves are drawn on the fly
// they cannot accept css variables without needing to parse them off the element with getComputedStyle
// there is a ticket to look into the best way to handle this since it would be a decently heavy compute task
// https://linear.app/openphone/issue/PRODU-624/review-and-improve-how-audio-visualizer-handles-bar-and-background

function OngoingCallWaveform({
  analyserNode,
  isActive,
  width = 600,
  height = 66,
  barWidth = 4,
  barGap = 8,
  minBarHeight = 12,
  maxBars = 16,
  backgroundColor = 'rgb(14, 14, 21)',
  barColor = 'rgb(114, 43, 255)',
  inactiveBarColor = '#505062',
  smoothingFactor = 0.8,
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !analyserNode) {
      return
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount)

    // draw a rounded rectangle using the canvas 2d api
    const drawRoundedBar = (x: number, y: number, width: number, height: number) => {
      const radius = width / 2
      ctx.beginPath()
      ctx.moveTo(x + radius, y)
      ctx.lineTo(x + width - radius, y)
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
      ctx.lineTo(x + width, y + height - radius)
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
      ctx.lineTo(x + radius, y + height)
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
      ctx.lineTo(x, y + radius)
      ctx.quadraticCurveTo(x, y, x + radius, y)
      ctx.closePath()
      ctx.fill()
    }

    // get the frequency value based on the index in the array
    const getFrequencyValue = (frequencies: Uint8Array, index: number, total: number) => {
      const linearIndex = Math.floor((index / total) * (frequencies.length / 4))
      const logIndex = Math.floor(Math.pow(frequencies.length / 4, index / total))
      const centerWeight = 1 - Math.abs((index - total / 2) / total)
      const mixedIndex = Math.floor(
        linearIndex * centerWeight + logIndex * (1 - centerWeight),
      )
      return frequencies[mixedIndex] || 2 // 2 is the minimum value we want to return so we always display something
    }
    const baseFrequencyTimers = new Array(maxBars).fill(0) // Track how long each bar is at base frequency
    const BASE_FREQUENCY = 2
    const TIME_THRESHOLD = 0.75

    // recursively draw the waveform based on the frequency data
    // as long as the component is active
    const drawWaveform = () => {
      animationRef.current = requestAnimationFrame(drawWaveform)
      analyserNode.getByteFrequencyData(dataArray)

      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const centerY = canvas.height / 2
      const centerX = canvas.width / 2
      const bands: number[] = []
      const bandsValues: { value1: number; value2: number }[] = [] // Store both frequency values

      for (let i = 0; i < maxBars; i++) {
        const value1 = getFrequencyValue(dataArray, i, maxBars)
        const value2 = getFrequencyValue(dataArray, i + 0.5, maxBars)

        bandsValues.push({ value1, value2 }) // Store values for later use

        const baseValue = Math.max(value1, value2) * 0.8 + ((value1 + value2) / 2) * 0.2
        const variance = Math.random() * 6 - 3

        bands[i] =
          bands[i] !== undefined
            ? (bands[i] as number) * smoothingFactor +
              (baseValue + variance) * (1 - smoothingFactor)
            : baseValue
      }

      for (let i = 0; i < maxBars; i++) {
        const value = bands[i]
        if (!value) {
          continue
        }

        const percent = value / 255
        const barHeight = Math.max(
          minBarHeight,
          Math.pow(percent, 0.7) * (canvas.height * 0.6),
        )

        // Check if the bar has been at base frequency for too long
        if (
          bandsValues[i]?.value1 === BASE_FREQUENCY &&
          bandsValues[i]?.value2 === BASE_FREQUENCY
        ) {
          baseFrequencyTimers[i]++ // Increment timer
        } else {
          baseFrequencyTimers[i] = 0 // Reset if frequency rises
        }

        // Determine bar color
        const barShouldBeGrey = baseFrequencyTimers[i] >= TIME_THRESHOLD
        ctx.fillStyle = barShouldBeGrey ? inactiveBarColor : barColor

        // Left side bars
        const leftX = centerX - (i + 1) * (barWidth + barGap)
        drawRoundedBar(leftX, centerY - barHeight / 2, barWidth, barHeight)

        // Right side bars
        const rightX = centerX + i * (barWidth + barGap)
        drawRoundedBar(rightX, centerY - barHeight / 2, barWidth, barHeight)
      }
    }

    if (isActive) {
      drawWaveform()
    }

    return () => {
      // ensure the animation stops when the component unmounts
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [
    analyserNode,
    isActive,
    width,
    height,
    barWidth,
    barGap,
    minBarHeight,
    maxBars,
    smoothingFactor,
    backgroundColor,
    barColor,
    inactiveBarColor,
  ])

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      data-testid="ongoing-call-waveform"
      aria-label="Ongoing call waveform"
    />
  )
}

export default OngoingCallWaveform
